import { Listbox, ListboxOption } from '@headlessui/react'

import { useEffect, useState } from 'react'

import type { Option, Value } from './types'
import { SelectListboxShell } from './SelectListboxShell'
import { optionClassName } from './optionClasses'

type FlatMenuSelectProps<T extends Value> = {
  onChange: (option: Option<T>) => void
  defaultValue: Option<T>
  options: Option<T>[]
}

export default function FlatMenuSelect<T extends Value>({
  onChange,
  defaultValue,
  options,
}: FlatMenuSelectProps<T>) {
  const [selected, setSelected] = useState<Option<T>>(
    options.find((opt) => opt.value === defaultValue.value) ?? defaultValue
  )

  // Update selected value from outside if defaultValue changes (as in the case of a language change)
  useEffect(() => {
    const next = options.find((opt) => opt.value === defaultValue.value) ?? defaultValue
    setSelected(next)
  }, [defaultValue, options])

  const handleChange = (option: Option<T> | null) => {
    if (option) {
      setSelected(option)
      onChange(option)
    }
  }

  return (
    <Listbox value={selected} onChange={handleChange}>
      <SelectListboxShell selected={selected}>
        {options.map((option) => {
          const isSelected = option.value === selected.value
          return (
            <ListboxOption
              key={option.value}
              value={option}
              disabled={isSelected}
              className={optionClassName(isSelected)}
            >
              {option.label}
            </ListboxOption>
          )
        })}
      </SelectListboxShell>
    </Listbox>
  )
}
